import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { trackLead } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRIES = ["USA", "UK", "Canada", "Australia", "Ireland", "New Zealand", "Europe", "Dubai", "Other"];

export default function Inquiry() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "USA", course: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      trackLead({ method: "form", country: form.country });
      toast.success("Inquiry received! We'll reach out within 24 hours.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", country: "USA", course: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send inquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="inquiry" className="py-20 md:py-28 bg-[#c92e34] text-white relative overflow-hidden" data-testid="inquiry-section">
      <div className="absolute inset-0 opacity-[0.12]" style={{
        backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
        backgroundSize: "60px 60px"
      }} />

      <div className="max-w-6xl mx-auto px-6 sm:px-10 relative grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-block bg-white text-[#c92e34] text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border-2 border-[#282829] -rotate-2">Free Counselling</span>
          <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9]">
            Tell us your<br /><span className="font-handwritten text-white inline-block -rotate-2">dream</span>.
          </h2>
          <p className="mt-5 text-white/90 max-w-md">
            Drop your details — a senior counsellor will reach out within 24 hours.
          </p>

          <div className="mt-6 bg-white text-[#282829] border-2 border-[#282829] rounded-2xl p-4 max-w-md" data-testid="online-services-note">
            <div className="text-[10px] font-bold tracking-widest uppercase text-[#c92e34]">Outside Nagpur?</div>
            <div className="font-bold text-sm md:text-base mt-1">
              We provide <span className="text-[#c92e34]">100% online counselling</span> for students across India & abroad.
            </div>
            <div className="text-xs text-[#282829]/65 mt-1">
              Video call · WhatsApp · Email — same dedicated guidance, zero travel needed.
            </div>
          </div>

          <div className="mt-8 space-y-3">
            <div className="bg-white text-[#282829] border-2 border-[#282829] rounded-2xl p-4">
              <div className="text-[10px] font-bold tracking-widest uppercase text-[#282829]/50">Counselling Hours</div>
              <div className="font-bold text-base">10:30 AM – 6:30 PM · Mon to Sat</div>
            </div>
            <div className="bg-[#282829] text-white border-2 border-[#282829] rounded-2xl p-4">
              <div className="text-[10px] font-bold tracking-widest uppercase text-white/70">Nagpur Office</div>
              <div className="font-bold text-base">Plot 20, First Floor, Near Samata Layout Garden, North Ambazari Road, Nagpur</div>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white text-[#282829] border-2 border-[#282829] rounded-3xl p-6 md:p-8 brutal-shadow-ink"
          data-testid="inquiry-form"
        >
          {done && (
            <div className="mb-5 flex items-center gap-2 bg-[#282829] text-white text-sm font-bold px-4 py-2 rounded-full border-2 border-[#282829]">
              <CheckCircle2 size={16} /> We received your details!
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name" name="name" value={form.name} onChange={onChange} placeholder="Your name" required />
            <Field label="Email" type="email" name="email" value={form.email} onChange={onChange} placeholder="you@email.com" required />
            <Field label="Phone" name="phone" value={form.phone} onChange={onChange} placeholder="+91 ..." required />
            <div>
              <label className="block text-[11px] font-bold tracking-widest uppercase text-[#282829]/60 mb-1.5">Country</label>
              <select
                name="country"
                value={form.country}
                onChange={onChange}
                data-testid="inquiry-country"
                className="w-full px-4 py-3 bg-white border-2 border-[#282829] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#c92e34]"
              >
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <Field label="Course / Program (optional)" name="course" value={form.course} onChange={onChange} placeholder="e.g. MS in Computer Science" className="mt-4" />

          <div className="mt-4">
            <label className="block text-[11px] font-bold tracking-widest uppercase text-[#282829]/60 mb-1.5">Message (optional)</label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              rows={3}
              data-testid="inquiry-message"
              placeholder="Tell us a bit about you, target intake, etc."
              className="w-full px-4 py-3 bg-white border-2 border-[#282829] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#c92e34]"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="inquiry-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#c92e34] hover:bg-[#282829] text-white font-bold px-6 py-4 rounded-full border-2 border-[#282829] brutal-shadow-ink disabled:opacity-60 transition-colors"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Book My Free Counselling <Send size={16} /></>}
          </button>
          <p className="mt-3 text-xs text-[#282829]/55">By submitting, you agree to be contacted by Shah Overseas counsellors.</p>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", required = false, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-bold tracking-widest uppercase text-[#282829]/60 mb-1.5">
        {label}{required && <span className="text-[#c92e34]"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={`inquiry-${name}`}
        className="w-full px-4 py-3 bg-white border-2 border-[#282829] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#c92e34]"
      />
    </div>
  );
}
